import React from 'react';
import { observer } from 'mobx-react';
import {
  Button, Card, CardContent, CardHeader, Grid, Theme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useTranslation } from '@gamiphy/react-i18n';
import { UserStore } from '@gamiphy/loyalty-station-desktop/store/user';
import { UnavowedPopup } from '@gamiphy/loyalty-station-desktop/components/popups/unavowed';
import { Pages, UiStore } from '@gamiphy/loyalty-station-desktop/store/ui';
import { WelcomeGiftStore, WelcomeGiftTypes } from '@gamiphy/loyalty-station-desktop/store/welcomeGift';
import { WelcomeReward } from '@gamiphy/loyalty-station-desktop/components/welcomeGift/reward';
import { AppWelcomeGiftStore } from '@gamiphy/loyalty-station-desktop/store/appWelcomeGift';

const useStyles = makeStyles<Theme, { page: Pages }>((theme) => ({
  root: {
    background: ({ page }) => (page === Pages.welcomeGift ? theme.palette.primary.main : '#fff'),
    color: theme.palette.text.secondary,
    textAlign: 'center',
  },
}));

const UnlockedCard = observer(() => {
  const classes = useStyles({ page: UiStore.currentPage });
  const { t } = useTranslation();
  const [popupOpen, setPopupOpen] = React.useState(false);

  const title = React.useMemo(() => {
    switch (WelcomeGiftStore.type) {
      case WelcomeGiftTypes.discount:
        if (AppWelcomeGiftStore.welcomeGift?.minPurchaseAmount) {
          return t('welcomeGift.discount.unlocked.titleWithMinOrderValue', {
            defaultValue: 'Welcome coupon unlocked on purchases over {{minOrderValue,currency}}',
            minOrderValue: AppWelcomeGiftStore.welcomeGift?.minPurchaseAmount,
          });
        }
        return t('welcomeGift.discount.unlocked.title', { defaultValue: 'Welcome coupon unlocked' });

      case WelcomeGiftTypes.points:
        return t('welcomeGift.points.unlocked.title', { defaultValue: 'Welcome points unlocked' });
      default:
        return undefined;
    }
  }, [WelcomeGiftStore.type, AppWelcomeGiftStore.welcomeGift]);

  const redeemTxt = React.useMemo(() => {
    switch (WelcomeGiftStore.type) {
      case WelcomeGiftTypes.discount:
        return t('welcomeGift.discount.redeem.cta', { defaultValue: 'Redeem coupon' });
      case WelcomeGiftTypes.points:
        return t('welcomeGift.points.redeem.cta', { defaultValue: 'Redeem {{, gems}}' });
      default:
        return undefined;
    }
  }, [WelcomeGiftStore.type]);

  const onRedeem = React.useCallback(() => {
    if (UserStore.isRealUser) {
      UiStore.setPage(Pages.welcomeGift);
    } else {
      setPopupOpen(true);
    }
  }, [UserStore.isRealUser]);

  return (
    <Card className={classes.root}>
      {UiStore.currentPage === Pages.welcomeGift && (
        <CardHeader title={title} />
      )}
      <CardContent>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <WelcomeReward />
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="secondary"
              onClick={onRedeem}
              fullWidth
            >
              {redeemTxt}
            </Button>
          </Grid>
        </Grid>
        <UnavowedPopup
          open={popupOpen}
          onClose={() => setPopupOpen(false)}
          title={t('unavowedPopup.rewardTitle', 'Sign up now to redeem your coupon for exclusive discounts!')}
        />
      </CardContent>
    </Card>
  );
});

export default UnlockedCard;
